"use client";

import { Pause, Play, SkipBack, SkipForward } from "lucide-react";
import { m } from "motion/react";
import Image from "next/image";
import { forwardRef, memo, useCallback, useMemo } from "react";
import { TVOverlay } from "@/components/tv-overlay";
import {
  ANIMATION_CONFIGS,
  ARCADE_STYLES,
  HIBERNATION_POKU_FILTER_LIVING,
  KANSEI_DEAD,
} from "@/constants/arcade-styles";
import { useSoundEffects, useSoundtrackDirector } from "@thehoneyjar/sound-engine";

export interface TetrisAudioState {
  isPlaying: boolean;
  currentTrack: any;
  trackName: string;
}

interface MusicPlayerProps {
  audioState?: TetrisAudioState;
  onPlayPause?: () => void;
  onBearClick?: () => void;
}

const VISUALIZER_BAR_HEIGHTS = [0.3, 0.5, 0.7, 0.9, 1, 0.8, 1, 0.9, 0.7, 0.5, 0.3];
const FLICKER_VARIANTS = ["a", "b", "c"] as const;
const FLICKER_DURATIONS = { a: 13.7, b: 18.3, c: 22.9 } as const;

// GPU-accelerated visualizer bar component — AFTER HOURS register:
// amber gradient swapped to moondust grey (single-amber-stamp discipline).
// When playing, bars run the LIVING flicker (the music is alive but barely);
// when paused, bars hold tiny without flicker so the signal reads dead.
const VisualizerBar = memo(
  ({ height, index, isPlaying }: { height: number; index: number; isPlaying: boolean }) => {
    const flickerVariant = FLICKER_VARIANTS[index % FLICKER_VARIANTS.length];
    const barDuration =
      ANIMATION_CONFIGS.VISUALIZER.durations[index % ANIMATION_CONFIGS.VISUALIZER.durations.length];
    const flickerKeyframeName = `kansei-flicker-living-${flickerVariant}`;
    const flickerDuration = FLICKER_DURATIONS[flickerVariant];
    const animation = useMemo(
      () =>
        isPlaying
          ? `barScale ${barDuration}s steps(4) infinite, ${flickerKeyframeName} ${flickerDuration}s infinite`
          : undefined,
      [barDuration, flickerDuration, flickerKeyframeName, isPlaying],
    );
    const animationDelay = useMemo(
      () => (isPlaying ? `${index * 0.1}s, 0s` : undefined),
      [index, isPlaying],
    );

    return (
      <div
        className="flex-1 origin-bottom bg-gradient-to-t from-zinc-600 to-transparent"
        style={{
          height: `${height * 60}px`,
          transform: isPlaying ? "scaleY(1)" : "scaleY(0.1)",
          willChange: "transform",
          animation,
          animationDelay,
          transition: "transform 0.3s ease-out",
        }}
      />
    );
  },
);

VisualizerBar.displayName = "VisualizerBar";

// Main music player component
export const MusicPlayer = memo(
  forwardRef<any, MusicPlayerProps>(({ onPlayPause, onBearClick }, _ref) => {
    const { playSound } = useSoundEffects();
    const { status, play, pause, setMasterEnabled, setMusicEnabled } = useSoundtrackDirector();

    const isPlaying = status.isPlaying;
    const trackTitle = status.title || "press play";
    const trackSubtitle = isPlaying ? "Now Playing" : trackTitle !== "press play" ? "Paused" : "No Track";

    const handlePlayPause = useCallback(async () => {
      if (onPlayPause) {
        onPlayPause();
        return;
      }

      if (isPlaying) {
        await pause(true);
      } else {
        // Flip gates only — the director's autoplay effect detects the
        // state change and calls playMusic. Calling play() here too
        // would race and double-fire the track.
        setMasterEnabled(true);
        setMusicEnabled(true);
      }
    }, [isPlaying, onPlayPause, pause, setMasterEnabled, setMusicEnabled]);

    return (
      <div className="relative rounded-md p-2" style={ARCADE_STYLES.TV_FRAME}>
        {/* Interactive Henlo Skate Bear */}
        <m.div
          className="absolute -bottom-4 left-0 z-10 cursor-pointer"
          onClick={onBearClick}
          whileHover={ANIMATION_CONFIGS.BEAR_HOVER}
          whileTap={ANIMATION_CONFIGS.BEAR_TAP}
        >
          <Image
            src="/lobby/music.png"
            alt="Henlo Boombox"
            width={120}
            height={120}
            className="object-contain kansei-flicker-living-c"
            style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
          />
        </m.div>

        <div className="relative overflow-hidden rounded-md" style={ARCADE_STYLES.TICKER_CONTAINER}>
          <TVOverlay intensity="light" />

          <div className="relative">
            {/* Background Visualizer with GPU acceleration */}
            <div className="absolute inset-0 flex items-end justify-center gap-[2px] opacity-20">
              {VISUALIZER_BAR_HEIGHTS.map((height, i) => (
                <VisualizerBar key={i} height={height} index={i} isPlaying={isPlaying} />
              ))}
            </div>

            <div className="relative flex w-full items-center justify-between p-4 pl-36">
              {/* Track Info — moondust zinc, LIVING register (the music is
                  broadcasting). Subtitle on -living-c, title on -living-b. */}
              <div>
                <h4 className="kansei-flicker-living-c font-pressStart2P text-[6px] uppercase text-zinc-600">
                  {trackSubtitle}
                </h4>
                <p className="kansei-flicker-living-b mt-1 font-pressStart2P text-[8px] uppercase text-zinc-500">
                  {trackTitle}
                </p>
              </div>

              {/* Playback Controls — recessed control deck. The 3 buttons
                  read as a connected piece of equipment, not floating
                  icons. Cobalt rim, dark inner gradient, scanline residue,
                  cobalt-tinted icons. */}
              <div
                className="relative flex items-center gap-3 overflow-hidden rounded-[3px] px-3 py-1.5"
                style={{
                  // Layer scanline residue ON TOP of the dark gradient.
                  // Single backgroundImage (multi-image) — first image
                  // renders on top, second below. Using `background`
                  // shorthand + `backgroundImage` override would replace
                  // the gradient and the deck would read transparent.
                  backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, linear-gradient(135deg, #08080a 0%, #11111a 100%)`,
                  boxShadow: `
                    inset 0 1px 2px rgba(0, 0, 0, 0.7),
                    inset 0 -1px 0 rgba(62, 97, 250, 0.18),
                    0 1px 0 rgba(255, 255, 255, 0.03)
                  `.trim(),
                  border: "1px solid rgba(62, 97, 250, 0.22)",
                }}
              >
                <m.button
                  className="relative z-10 transition-colors"
                  style={{ color: "rgba(155, 176, 240, 0.7)" }}
                  whileHover={{ color: "rgba(155, 176, 240, 1)" } as any}
                  whileTap={{
                    scale: 0.95,
                    y: 1,
                    transition: {
                      type: "spring",
                      bounce: 0,
                      duration: 0.1,
                    },
                  }}
                  onClick={() => {
                    void playSound("buttonClick");
                  }}
                >
                  <SkipBack className="size-3" />
                </m.button>

                <m.button
                  onClick={() => {
                    void handlePlayPause();
                  }}
                  className="relative z-10 transition-colors"
                  style={{
                    color: isPlaying ? "rgba(200, 215, 255, 1)" : "rgba(155, 176, 240, 0.85)",
                  }}
                  whileHover={{ color: "rgba(200, 215, 255, 1)" } as any}
                  whileTap={{
                    scale: 0.95,
                    y: 1,
                    transition: {
                      type: "spring",
                      bounce: 0,
                      duration: 0.1,
                    },
                  }}
                >
                  {isPlaying ? (
                    <Pause className="size-5" />
                  ) : (
                    <Play className="size-5" />
                  )}
                </m.button>

                <m.button
                  className="relative z-10 transition-colors"
                  style={{ color: "rgba(155, 176, 240, 0.7)" }}
                  whileHover={{ color: "rgba(155, 176, 240, 1)" } as any}
                  whileTap={{
                    scale: 0.95,
                    y: 1,
                    transition: {
                      type: "spring",
                      bounce: 0,
                      duration: 0.1,
                    },
                  }}
                  onClick={() => {
                    void playSound("buttonClick");
                  }}
                >
                  <SkipForward className="size-3" />
                </m.button>
              </div>
            </div>
          </div>
        </div>

        {/* Add keyframe animations */}
        <style>{ANIMATION_CONFIGS.VISUALIZER.keyframes}</style>
      </div>
    );
  }),
);

MusicPlayer.displayName = "MusicPlayer";
